import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, History as HistoryIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import DebtCard from "@/components/DebtCard";
import EmptyState from "@/components/EmptyState";
import api from "@/lib/api";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "outstanding", label: "Outstanding" },
  { key: "overdue", label: "Overdue" },
  { key: "paid", label: "Paid" },
];

export default function HistoryPage() {
  const [status, setStatus] = useState("all");
  const [direction, setDirection] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  const { data: debts = [], isLoading } = useQuery({
    queryKey: ["debts", status, search, sort, direction],
    queryFn: async () =>
      (await api.get("/debts", { params: { status, search, sort, direction } })).data,
  });

  return (
    <div className="pb-4">
      <h1 className="font-display text-2xl font-bold text-foreground">History</h1>

      <div className="mt-4 grid grid-cols-3 gap-2 rounded-2xl bg-secondary p-1">
        {[
          { key: "all", label: "All" },
          { key: "owed_to_me", label: "Owed to me" },
          { key: "i_owe", label: "I owe" },
        ].map((d) => (
          <button
            key={d.key}
            data-testid={`history-direction-${d.key}`}
            onClick={() => setDirection(d.key)}
            className={`h-9 rounded-xl text-sm font-semibold transition-all ${direction === d.key ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="mt-3 relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          data-testid="history-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by person or reason"
          className="h-12 pl-11 rounded-xl bg-white"
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="flex-1 flex gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`history-filter-${f.key}`}
              onClick={() => setStatus(f.key)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${
                status === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-white text-foreground border-border"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="history-sort" className="h-10 rounded-xl bg-white w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="highest">Highest amount</SelectItem>
            <SelectItem value="due_date">Due date</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2.5">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}
        </div>
      ) : debts.length === 0 ? (
        <EmptyState testId="history-empty" icon={HistoryIcon} title="Nothing here yet" subtitle="Debts matching this filter will appear here." />
      ) : (
        <div className="mt-4 space-y-2.5">
          {debts.map((d) => <DebtCard key={d.id} debt={d} />)}
        </div>
      )}
    </div>
  );
}
